import React, {useState, MouseEvent, FC, useEffect} from 'react';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';

import Ellipsis from '../../components/common/Ellipsis';
import {ITEM_DEFAULT_SIZE, VirtualizedListbox} from '../game-select/VirtualizedListbox';
import {CODES} from '../game-select/GameSelect';
import RadioInput from './RadioInput';
import RadioOption from './RadioOption';
import RadioAutocompleteList from './RadioAutocompleteList';
import useLocalization from '../../hooks/useLocalization';

import {AutocompleteListProps} from '../game-select/types';
import {Option} from '../../types';

import {StyledPopover} from './styles';

export interface RadioGameSelectProps extends Partial<AutocompleteListProps> {
    options: Option[];
    value: Option;
    combinedOption?: Option;
}

const RadioGameSelect: FC<RadioGameSelectProps> = ({
    onChange,
    value: activeOption,
    combinedOption,
    ...props
}: RadioGameSelectProps) => {
    const getMessage = useLocalization();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const initialOption: Option = {
        label: getMessage(CODES.noOptions),
        value: ''
    };
    const [internalValue, setInternalValue] = useState(initialOption);

    const handleClick = (event: MouseEvent<HTMLElement>) => {
        setAnchorEl(anchorEl ? null : event.currentTarget);

        if (anchorEl) {
            onChange(event, internalValue, 'select-option');
        }
    };

    const handleCombinedChange = _event => {
        setInternalValue(combinedOption);
    };

    const handleChange = (_event, option: Option) => {
        setInternalValue(option);
    };

    const open = Boolean(anchorEl);

    useEffect(() => {
        if (activeOption && activeOption.value) {
            setInternalValue(activeOption);
        }
    }, [activeOption]);

    return (
        <>
            <RadioInput open={open} onClick={handleClick}>
                <>
                    <Typography noWrap variant="body1" component="span">
                        <Ellipsis>{internalValue.label}</Ellipsis>
                    </Typography>
                    {internalValue.value !== 'all' && (
                        <Box mx={4}>
                            <Typography
                                noWrap
                                variant="body1"
                                color="textSecondary"
                                component="span">
                                <Ellipsis>{internalValue.value}</Ellipsis>
                            </Typography>
                        </Box>
                    )}
                </>
            </RadioInput>
            <StyledPopover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClick}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center'
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center'
                }}>
                <Box mt={2}>
                    {combinedOption && (
                        <>
                            <Box display="flex" mx={4} onClick={handleCombinedChange}>
                                <RadioOption
                                    key={combinedOption.value}
                                    selected={internalValue.value === combinedOption.value}
                                    {...combinedOption}
                                />
                            </Box>
                            <Box mx={4} my={1}>
                                <Divider />
                            </Box>
                        </>
                    )}
                    <RadioAutocompleteList
                        {...props}
                        value={internalValue}
                        multiple={false}
                        onChange={handleChange}
                        ListboxComponent={VirtualizedListbox}
                        ListboxProps={{
                            VariableSizeListProps: {
                                itemSize: () => ITEM_DEFAULT_SIZE
                            }
                        }}
                        resultsLabel={getMessage(CODES.myGames)}
                        placeholder={getMessage(CODES.searchPlaceholder)}
                        noOptionsText={getMessage(CODES.noOptions)}
                        renderOption={(option, {selected}) => (
                            <RadioOption key={option.value} selected={selected} {...option} />
                        )}
                    />
                </Box>
            </StyledPopover>
        </>
    );
};

export default RadioGameSelect;
